import React from 'react';
import { VdpElement, VdpTemplate, Unit, CSVData } from '../../types';
import { DollarSign, Eye, EyeOff, Plus, Tag, Settings, Sliders, Check, Layers, Sparkles } from 'lucide-react';
import { formatUnit, parseUnit } from '../../utils/units';

interface PriceTagSidebarManagerProps {
  template: VdpTemplate;
  onUpdateTemplate: (updates: Partial<VdpTemplate>) => void;
  onUpdateElement: (id: string, updates: Partial<VdpElement>) => void;
  onAddElement: (type: any, initialProps?: Partial<VdpElement>) => void;
  onSelectElement: (id: string) => void;
  appUnit: Unit;
  setAppUnit: (unit: Unit) => void;
  csvData: CSVData | null;
}

export const PriceTagSidebarManager: React.FC<PriceTagSidebarManagerProps> = React.memo(({
  template,
  onUpdateTemplate,
  onUpdateElement,
  onAddElement,
  onSelectElement,
  appUnit,
  setAppUnit,
  csvData
}) => {
  // Find price tag / currency elements in the template
  const priceElements = template.elements.filter(
    el => el.type === 'currency' || (el.columnName && el.columnName.toLowerCase().includes('price'))
  );

  const priceTag = priceElements[0] || null;
  const isHidden = priceTag ? (priceTag.disabled || priceTag.isSkipped) : false;

  const handleTogglePriceVisibility = () => {
    if (!priceTag) {
      // Create price tag if it doesn't exist
      onAddElement('currency', {
        x: 12,
        y: 45,
        width: 100,
        height: 24,
        staticValue: '19.99',
        currencySymbol: '$',
        decimalPlaces: 2,
        formatThousands: true,
        fontSize: 12,
        fontFamily: 'Helvetica',
        color: '#166534',
        isBold: true,
        disabled: false,
        isSkipped: false
      });
      return;
    }

    // Toggle disabled / isSkipped across all price elements
    const newHiddenState = !isHidden;
    priceElements.forEach(el => {
      onUpdateElement(el.id, {
        disabled: newHiddenState,
        isSkipped: newHiddenState
      });
    });
  };

  const handleAddDefaultPriceTag = () => {
    onAddElement('currency', {
      x: 12,
      y: 45,
      width: 100,
      height: 24,
      staticValue: '19.99',
      currencySymbol: '$',
      decimalPlaces: 2,
      formatThousands: true,
      fontSize: 12,
      fontFamily: 'Helvetica',
      color: '#166534',
      isBold: true,
      backgroundColor: '#dcfce7',
      fillColor: '#dcfce7',
      borderWidth: 1,
      borderColor: '#166534',
      borderRadius: 6
    });
  };

  return (
    <div className="p-4 flex-1 overflow-auto space-y-4 custom-scrollbar">
      {/* Price Tag Configuration Card */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3.5 shadow-md space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-sm shrink-0 border border-emerald-500/30">
              <Tag className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-zinc-100 flex items-center gap-1.5">
                Sticker Price Tag Manager
              </h3>
              <p className="text-[10px] text-zinc-400">Manage price formatting & visibility on sticker</p>
            </div>
          </div>

          {priceTag && (
            <button
              onClick={handleTogglePriceVisibility}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer ${
                isHidden
                  ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30'
                  : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30'
              }`}
              title={isHidden ? 'Click to show price tag on sticker' : 'Click to hide price tag from sticker'}
            >
              {isHidden ? (
                <>
                  <EyeOff className="w-3.5 h-3.5 text-rose-400" />
                  <span>Hidden</span>
                </>
              ) : (
                <>
                  <Eye className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Visible</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* If price tag exists */}
        {priceTag ? (
          <div className="space-y-3">
            {/* Price Preview Pill & Select Button */}
            <div className="flex items-center justify-between bg-zinc-950/80 p-2.5 rounded-lg border border-zinc-800">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded font-bold font-mono ${
                  isHidden ? 'bg-zinc-800 text-zinc-500 line-through' : 'bg-emerald-950 text-emerald-300 border border-emerald-500/30'
                }`}>
                  {priceTag.currencySymbol || '$'}{priceTag.staticValue || '19.99'}
                </span>
                <span className="text-[11px] text-zinc-400 truncate max-w-[110px]">
                  {priceTag.columnName ? `Bound: ${priceTag.columnName}` : 'Static Price'}
                </span>
              </div>
              <button
                onClick={() => onSelectElement(priceTag.id)}
                className="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded text-[11px] font-medium transition-colors flex items-center gap-1"
              >
                <Sliders className="w-3 h-3 text-emerald-400" />
                <span>Edit Props</span>
              </button>
            </div>

            {/* Quick Currency Symbol Switcher */}
            <div>
              <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Currency Symbol</label>
              <div className="flex flex-wrap items-center gap-1.5">
                {['$', '₹', '€', '£', '¥', 'A$', 'C$', 'CHF', 'AED'].map(sym => (
                  <button
                    key={sym}
                    type="button"
                    onClick={() => onUpdateElement(priceTag.id, { currencySymbol: sym })}
                    className={`min-w-[32px] h-7 px-2 flex items-center justify-center rounded-md text-xs font-bold transition-all shadow-xs cursor-pointer ${
                      (priceTag.currencySymbol || '$') === sym
                        ? 'bg-emerald-600 text-white shadow-emerald-900/30 ring-1 ring-emerald-400'
                        : 'bg-zinc-900 text-zinc-300 hover:text-white hover:bg-zinc-800 border border-zinc-750'
                    }`}
                    title={`Set currency: ${sym}`}
                  >
                    {sym}
                  </button>
                ))}
              </div>
            </div>

            {/* Data Source or Static Price Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300 block">Price Data Source</label>
              {csvData?.headers && csvData.headers.length > 0 ? (
                <select
                  value={priceTag.columnName || ''}
                  onChange={e => onUpdateElement(priceTag.id, { columnName: e.target.value, isStatic: !e.target.value })}
                  className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200 focus:ring-1 focus:ring-emerald-500 outline-none"
                >
                  <option value="">-- Static Price --</option>
                  {csvData.headers.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              ) : null}

              {!priceTag.columnName && (
                <input
                  type="text"
                  value={priceTag.staticValue || ''}
                  onChange={e => onUpdateElement(priceTag.id, { staticValue: e.target.value, text: e.target.value })}
                  placeholder="Price amount e.g. 19.99"
                  className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200 focus:ring-1 focus:ring-emerald-500 outline-none"
                />
              )}
            </div>

            {/* Decimals & Thousands Separator Quick Toggles */}
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-[11px] font-semibold text-zinc-300 block mb-1">Decimals</label>
                <div className="grid grid-cols-2 gap-1 bg-zinc-950 p-1 rounded-md border border-zinc-800">
                  <button
                    onClick={() => onUpdateElement(priceTag.id, { decimalPlaces: 0 })}
                    className={`py-1.5 px-1 rounded text-center text-xs font-semibold transition-colors truncate cursor-pointer ${
                      (priceTag.decimalPlaces ?? 2) === 0 ? 'bg-emerald-600 text-white shadow-xs' : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    title="0 decimals e.g. $20"
                  >
                    0 ($20)
                  </button>
                  <button
                    onClick={() => onUpdateElement(priceTag.id, { decimalPlaces: 2 })}
                    className={`py-1.5 px-1 rounded text-center text-xs font-semibold transition-colors truncate cursor-pointer ${
                      (priceTag.decimalPlaces ?? 2) === 2 ? 'bg-emerald-600 text-white shadow-xs' : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    title="2 decimals e.g. $19.99"
                  >
                    2 ($19.99)
                  </button>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-semibold text-zinc-300 block mb-1">Thousand Sep.</label>
                <button
                  onClick={() => onUpdateElement(priceTag.id, { formatThousands: !(priceTag.formatThousands ?? true) })}
                  className={`w-full py-1.5 px-2.5 rounded-md border text-xs font-semibold transition-colors flex items-center justify-between cursor-pointer h-[34px] ${
                    (priceTag.formatThousands ?? true) ? 'bg-emerald-950/50 border-emerald-500/60 text-emerald-300 shadow-xs' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-300'
                  }`}
                  title="Format thousands with comma"
                >
                  <span>1,000</span>
                  {priceTag.formatThousands ?? true ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : null}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-2 space-y-2">
            <p className="text-xs text-zinc-400">No price tag found on this sticker template.</p>
            <button
              onClick={handleAddDefaultPriceTag}
              className="w-full py-2 px-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-bold shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-4 h-4" /> Add Price Tag to Sticker
            </button>
          </div>
        )}
      </div>

      {/* Sticker Template Properties Card */}
      <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3.5 shadow-md space-y-3">
        <h4 className="text-xs font-semibold text-zinc-200 flex items-center gap-1.5">
          <Settings className="w-3.5 h-3.5 text-blue-400" /> Sticker Canvas Properties
        </h4>

        {/* Dimension inputs */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-zinc-400 block mb-0.5">Width ({appUnit})</label>
            <input
              type="number"
              value={formatUnit(template.width || 300, appUnit)}
              onChange={e => onUpdateTemplate({ width: parseUnit(e.target.value, appUnit) })}
              className="w-full px-2.5 py-1 bg-zinc-950 border border-zinc-700 rounded text-xs text-zinc-200 focus:ring-1 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-400 block mb-0.5">Height ({appUnit})</label>
            <input
              type="number"
              value={formatUnit(template.height || 200, appUnit)}
              onChange={e => onUpdateTemplate({ height: parseUnit(e.target.value, appUnit) })}
              className="w-full px-2.5 py-1 bg-zinc-950 border border-zinc-700 rounded text-xs text-zinc-200 focus:ring-1 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Sticker Shape & Radius */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-zinc-400 block mb-0.5">Sticker Shape</label>
            <select
              value={template.shape || 'rectangle'}
              onChange={e => onUpdateTemplate({ shape: e.target.value as any })}
              className="w-full px-2 py-1 bg-zinc-950 border border-zinc-700 rounded text-xs text-zinc-200"
            >
              <option value="rectangle">Rectangle / Square</option>
              <option value="circle">Circle / Round</option>
              <option value="oval">Oval / Ellipse</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-zinc-400 block mb-0.5">Corner Radius (px)</label>
            <input
              type="number"
              min="0"
              max="50"
              value={template.cornerRadius || 0}
              onChange={e => onUpdateTemplate({ cornerRadius: parseFloat(e.target.value) || 0 })}
              className="w-full px-2.5 py-1 bg-zinc-950 border border-zinc-700 rounded text-xs text-zinc-200 focus:ring-1 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Background Color */}
        <div>
          <label className="text-[10px] text-zinc-400 block mb-0.5">Background Fill Color</label>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={template.backgroundColor || '#ffffff'}
              onChange={e => onUpdateTemplate({ backgroundColor: e.target.value })}
              className="w-7 h-7 p-0 border-0 rounded cursor-pointer bg-transparent"
            />
            <input
              type="text"
              value={template.backgroundColor || '#ffffff'}
              onChange={e => onUpdateTemplate({ backgroundColor: e.target.value })}
              className="w-full px-2.5 py-1 bg-zinc-950 border border-zinc-700 rounded text-xs text-zinc-200"
            />
          </div>
        </div>
      </div>
    </div>
  );
});
